import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus, FormSubmitHandler, Switch} from 'platform/components';
import {Box, Heading, HStack, Stack, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useCallback, useEffect, useMemo, useState} from 'react';
import {FieldErrors} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {noop, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {
  useGetBatchQuery,
  useListBatchVehiclesQuery,
  useUpdateBatchVehicleMutation,
} from '../../../store/importApi';
import {handleApiError} from '../../../utils/handleApiError';
import {CreatePageContext} from '../../Create/components/CreatePageContext';
import {VehicleFormType} from '../../Create/types/VehicleFormType';
import {useGetRequestVehicleFromFormData} from '../../Create/utils/useGetRequestVehicleFromFormData';
import {isBatchComplete} from '../utils/isBatchComplete';
import {mapVehicleItemToImportVehicle} from '../utils/mapVehicleItemToImportVehicle';
import {requestVehicleToResolvedVehicle} from '../utils/requestVehicleToResolvedVehicle';
import {resolvedVehicleToRequestVehicle} from '../utils/resolvedVehicleToRequestVehicle';
import {EditEquipmentModal} from './EditEquipmentModal';
import {EditSpecificationModal} from './EditSpecificationModal';
import {ImportResultLoadingView} from './ImportResultLoadingView';
import {VehicleImportRow} from './VehicleImportRow';

const VEHICLES_PAGE_LIMIT = 1000;
const BATCH_POLLING_INTERVAL_MS = 10_000;

interface ImportResultViewProps extends TestIdProps {
  batchId: string;
}

export function ImportResultView(props: ImportResultViewProps) {
  const formatDate = useDateTimeFormatter();
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);
  const [vehicleIdForEdit, setVehicleIdForEdit] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState(BATCH_POLLING_INTERVAL_MS);

  const {
    data: batch,
    isLoading: isBatchLoading,
    isError: isBatchError,
  } = useGetBatchQuery(isNotNil(props.batchId) ? {batchId: props.batchId} : skipToken, {
    pollingInterval,
  });

  useEffect(() => {
    setPollingInterval(BATCH_POLLING_INTERVAL_MS);
  }, [props.batchId]);

  useEffect(() => {
    if (isBatchError || (isNotNil(batch) && isBatchComplete(batch.status))) {
      setPollingInterval(0);
    }
  }, [batch, isBatchError]);

  const {
    data: vehiclesResponse,
    isLoading: isVehiclesLoading,
    isError: isVehiclesError,
  } = useListBatchVehiclesQuery(
    isNotNil(props.batchId)
      ? {
          batchId: props.batchId,
          page: 1,
          limit: VEHICLES_PAGE_LIMIT,
        }
      : skipToken,
    {
      skip: isNil(batch) || !isBatchComplete(batch.status),
    }
  );

  const vehicles = useMemo(
    () => (vehiclesResponse?.data ?? []).map(mapVehicleItemToImportVehicle),
    [vehiclesResponse]
  );

  const importedAt = useMemo(() => {
    if (!batch?.createdAt) {
      return '';
    }
    const parsed = parseDate(batch.createdAt);
    return parsed ? formatDate('dateTimeShort', parsed) : '';
  }, [batch?.createdAt, formatDate]);

  const isWaitingForComplete = isNotNil(batch) && !isBatchComplete(batch.status);
  const isLoading = isBatchLoading || isVehiclesLoading;
  const isError = isBatchError || isVehiclesError;
  const importName = batch?.name;
  const filteredVehicles = showErrorsOnly
    ? vehicles.filter((vehicle) => vehicle.rowStatus !== 'success')
    : vehicles;

  const requestVehicleForEdit = useMemo(
    () =>
      resolvedVehicleToRequestVehicle(
        vehicleIdForEdit
          ? vehiclesResponse?.data?.find((v) => v.id === vehicleIdForEdit)?.resolvedVehicle
          : null
      ),
    [vehicleIdForEdit, vehiclesResponse]
  );

  const [updateBatchVehicle] = useUpdateBatchVehicleMutation();
  const getRequestVehicleFromFormData = useGetRequestVehicleFromFormData();

  const handleEditFormSubmit = useCallback<FormSubmitHandler<VehicleFormType>>(
    (data) => {
      if (isNil(vehicleIdForEdit)) {
        return Promise.resolve();
      }
      const requestVehicle = getRequestVehicleFromFormData(data);
      const resolvedVehicle = requestVehicleToResolvedVehicle(requestVehicle);
      return updateBatchVehicle({
        batchId: props.batchId,
        vehicleId: vehicleIdForEdit,
        updateVehicleRequest: {resolvedVehicle},
      })
        .unwrap()
        .then(() => {
          setIsEditModalOpen(false);
          setIsEquipmentModalOpen(false);
          setVehicleIdForEdit(null);
        })
        .catch(handleApiError);
    },
    [vehicleIdForEdit, props.batchId, getRequestVehicleFromFormData, updateBatchVehicle]
  );

  const handleEditFormInvalidSubmit = useCallback((errors: FieldErrors<VehicleFormType>) => {
    const errorNames = Object.keys(errors);
    if (errorNames.length > 0) {
      const input = document.querySelector(`[data-name=${errorNames[0]}]`);
      input?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      });
    }
  }, []);

  const handleToggleRow = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const batchTitle = isNotNil(batch)
    ? `${i18n.t('page.import.labels.batch', {defaultValue: 'Batch'})} • ${batch.vehicleCount} ${i18n.t('page.import.labels.vehicles')}`
    : '';

  if (isWaitingForComplete && !isError) {
    return (
      <Box maxWidth={150} width="100%" data-testid={suffixTestId('wrapper', props)}>
        <ImportResultLoadingView
          importName={importName}
          data-testid={suffixTestId('loadingView', props)}
        />
      </Box>
    );
  }

  return (
    <>
      <Box width="100%" data-testid={suffixTestId('wrapper', props)}>
        <Card data-testid={suffixTestId('card', props)}>
          <Box padding={6}>
            <VStack spacing={6}>
              <DataStatus
                isLoading={isLoading}
                isError={isError}
                errorMessage={i18n.t('page.import.labels.loadError')}
                minHeight={20}
                data-testid={suffixTestId('dataStatus', props)}
              >
                <Stack
                  direction={['column', 'column', 'row']}
                  spacing={[2, 2, 4]}
                  justify={['flex-start', 'flex-start', 'space-between']}
                  align="flex-start"
                >
                  <Heading size={1}>{batchTitle}</Heading>
                  <VStack align="flex-end" spacing={1}>
                    <Text size="xSmall" color="secondary">
                      {i18n.t('page.import.labels.imported')}
                    </Text>
                    <Text size="xSmall">{importedAt}</Text>
                  </VStack>
                </Stack>

                <Switch
                  label={i18n.t('page.import.labels.showErrorsOnly')}
                  value={showErrorsOnly}
                  onChange={setShowErrorsOnly}
                  data-testid={suffixTestId('errorsOnlySwitch', props)}
                />

                <Box>
                  <Box
                    backgroundColor="palettes.neutral.10.100"
                    borderBottom="1px solid"
                    borderColor="palettes.neutral.30.100"
                    paddingVertical={2}
                    paddingHorizontal={4}
                  >
                    <HStack spacing={4} align="center">
                      <Box width={10}>
                        <Text size="xSmall" color="secondary" alternative>
                          {i18n.t('page.import.labels.rowNumber')}
                        </Text>
                      </Box>
                      <Text size="xSmall" color="secondary" alternative>
                        {i18n.t('page.import.labels.specification')}
                      </Text>
                    </HStack>
                  </Box>

                  {filteredVehicles.map((vehicle) => (
                    <VehicleImportRow
                      key={vehicle.id}
                      vehicle={vehicle}
                      isExpanded={expandedIds.has(vehicle.id)}
                      onToggle={() => handleToggleRow(vehicle.id)}
                      onEditSpecification={() => {
                        setVehicleIdForEdit(vehicle.id);
                        setIsEditModalOpen(true);
                      }}
                      onEditEquipment={() => {
                        setVehicleIdForEdit(vehicle.id);
                        setIsEquipmentModalOpen(true);
                      }}
                      data-testid={suffixTestId(`vehicle-${vehicle.id}`, props)}
                    />
                  ))}
                </Box>
              </DataStatus>
            </VStack>
          </Box>
        </Card>
      </Box>

      {isEditModalOpen || isEquipmentModalOpen ? (
        <CreatePageContext
          key={vehicleIdForEdit ?? 'empty'}
          onSubmit={handleEditFormSubmit}
          onInvalidSubmit={handleEditFormInvalidSubmit}
          onDebouncedChange={noop}
          vehicle={requestVehicleForEdit}
          market={batch?.market}
        >
          <EditSpecificationModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSubmit={handleEditFormSubmit}
            onInvalidSubmit={handleEditFormInvalidSubmit}
            data-testid={suffixTestId('editSpecModal', props)}
          />
          <EditEquipmentModal
            isOpen={isEquipmentModalOpen}
            onClose={() => setIsEquipmentModalOpen(false)}
            onSubmit={handleEditFormSubmit}
            onInvalidSubmit={handleEditFormInvalidSubmit}
            data-testid={suffixTestId('editEquipmentModal', props)}
          />
        </CreatePageContext>
      ) : null}
    </>
  );
}
